/**
 * Imperative Noddi Support Hub embed client.
 * React owns inputs; this module owns install / boot / identity / context lifecycle.
 *
 * Typed against Support Hub embed contract v1 (`widget-host.d.ts`).
 */

import { brands } from 'noddi-types';
import { customerWidgetSupportedLocales } from './locale';
import type {
  WidgetEnvironment,
  WidgetHostContext,
  WidgetIdentity,
  WidgetInitOptions,
  WidgetUpdateOptions
} from './widget-host';

const WIDGET_KEY = import.meta.env.VITE_NAVIO_WIDGET_KEY || '';

const WIDGET_API_URL = 'https://qgfaycwsangsqzpveoup.supabase.co/functions/v1';

const WIDGET_SCRIPT_URL = 'https://qgfaycwsangsqzpveoup.supabase.co/storage/v1/object/public/widget/widget.js';

const SCRIPT_ID = 'noddi-support-widget-script';

export type NoddiSupportWidgetBoot = {
  brand: string;
  locale: string;
  environment: WidgetEnvironment;
  sourceApp: 'customer';
  appVersion?: string;
  pathname: string;
};

export type NoddiSupportWidgetSyncState = {
  enabled: boolean;
  boot: NoddiSupportWidgetBoot;
  identity: WidgetIdentity | null;
  context: WidgetHostContext;
  /** Host override; defaults to false (knowledge search hidden). */
  enableKnowledgeSearch?: boolean;
  /** Host override; defaults to false (AI assistant hidden until we opt in). */
  enableAiChat?: boolean;
};

// Module lifecycle — reassigned across sync/open calls.
// eslint-disable-next-line no-restricted-syntax -- install flag
let installStarted = false;
// eslint-disable-next-line no-restricted-syntax -- boot flag
let booted = false;
// eslint-disable-next-line no-restricted-syntax -- last identity fingerprint
let lastIdentityKey: string | null = null;

const omitEmpty = <T extends Record<string, unknown>>(options: T): Partial<T> =>
  Object.fromEntries(
    Object.entries(options).filter(([, value]) => value !== undefined && value !== null && value !== '')
  ) as Partial<T>;

const identityKey = (identity: WidgetIdentity | null): string | null => {
  if (!identity) {
    return null;
  }
  return JSON.stringify({
    userId: identity.userId != null ? String(identity.userId) : '',
    name: identity.name ?? '',
    email: identity.email ?? '',
    phone: identity.phone ?? ''
  });
};

const ensureQueueStub = () => {
  if (typeof window === 'undefined') {
    return;
  }

  // Pre-boot stub matches the official embed snippet; the real widget.js replaces it.
  if (typeof window.NoddiWidget !== 'function') {
    const stub = function noddiWidgetStub(...args: unknown[]) {
      (stub.q = stub.q || []).push(args);
    } as Window['NoddiWidget'];
    stub.q = [];
    window.NoddiWidget = stub;
  }
};

const ensureScript = () => {
  if (typeof document === 'undefined' || installStarted) {
    return;
  }
  installStarted = true;
  ensureQueueStub();

  if (document.getElementById(SCRIPT_ID)) {
    return;
  }

  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = WIDGET_SCRIPT_URL;
  document.body.appendChild(script);
};

// The queue stub and the real WidgetHostCommands share a call signature; keep this loose.
const call = (command: string, options?: unknown) => {
  ensureQueueStub();
  return (window.NoddiWidget as unknown as (cmd: string, opts?: unknown) => unknown)(command, options);
};

const toBootContext = (boot: NoddiSupportWidgetBoot): WidgetHostContext =>
  omitEmpty({
    locale: boot.locale,
    environment: boot.environment,
    sourceApp: boot.sourceApp,
    appVersion: boot.appVersion,
    pathname: boot.pathname
  });

const toIdentityPayload = (identity: WidgetIdentity): WidgetIdentity => omitEmpty({ ...identity });

const resolveHostGate = (value: boolean | undefined): boolean => value ?? false;

const bootWidget = (
  options: NoddiSupportWidgetBoot,
  identity: WidgetIdentity | null,
  enableKnowledgeSearch: boolean,
  enableAiChat: boolean
) => {
  ensureScript();
  const initOptions: WidgetInitOptions = {
    widgetKey: WIDGET_KEY,
    apiUrl: WIDGET_API_URL,
    showButton: false,
    brand: options.brand,
    context: toBootContext(options),
    supportedLocales: customerWidgetSupportedLocales,
    enableKnowledgeSearch,
    enableAiChat,
    // Known visitor at boot so contact forms can prefill before a later identify.
    ...(identity ? { identity: toIdentityPayload(identity) } : {})
  };
  call('init', initOptions);
  booted = true;
  // Force a follow-up identify so hubs that only listen post-init still get the visitor.
  lastIdentityKey = null;
};

const isNoddiSupportWidgetConfigured = () => Boolean(WIDGET_KEY);

const noddiSupportWidgetBrands: readonly string[] = [brands.noddi, brands.dekkfix];

/** Brand allow-list and a configured key: without the key the widget never opens. */
export const isNoddiSupportWidgetEnabled = (brand: string) =>
  isNoddiSupportWidgetConfigured() && noddiSupportWidgetBrands.includes(brand);

export const syncNoddiSupportWidget = (state: NoddiSupportWidgetSyncState) => {
  if (!WIDGET_KEY) {
    return;
  }

  if (!state.enabled) {
    if (booted) {
      call('clearIdentity');
      lastIdentityKey = null;
    }
    return;
  }

  const enableKnowledgeSearch = resolveHostGate(state.enableKnowledgeSearch);
  const enableAiChat = resolveHostGate(state.enableAiChat);

  if (!booted) {
    bootWidget(state.boot, state.identity, enableKnowledgeSearch, enableAiChat);
  }

  const nextIdentityKey = identityKey(state.identity);
  if (nextIdentityKey !== lastIdentityKey) {
    if (state.identity) {
      call('identify', toIdentityPayload(state.identity));
      lastIdentityKey = nextIdentityKey;
    } else if (lastIdentityKey !== null) {
      // Stays booted; hub clears identity + noddi_chat_session
      call('clearIdentity');
      lastIdentityKey = null;
    }
  }

  const updateOptions: WidgetUpdateOptions = {
    brand: state.boot.brand,
    context: omitEmpty({
      ...toBootContext(state.boot),
      ...state.context
    }),
    supportedLocales: customerWidgetSupportedLocales,
    enableKnowledgeSearch,
    enableAiChat,
    // Keep identity on update so mid-session re-renders can re-prefill forms.
    ...(state.identity ? { identity: toIdentityPayload(state.identity) } : {})
  };
  call('update', updateOptions);
};

export const openNoddiSupportWidget = () => {
  if (!WIDGET_KEY) {
    return;
  }
  // Ensure the queue stub / script are installed so a click before the bridge
  // sync still reaches the hub (commands before ready are queued and replayed).
  ensureQueueStub();
  if (!booted) {
    ensureScript();
  }
  call('open');
};
